'use client';

import { useEffect, useSyncExternalStore } from 'react';
import type { WavespeedModel } from '@/lib/ai/wavespeed';

type Snapshot = { models: WavespeedModel[]; loading: boolean; stale: boolean; updatedAt?: string };
const initial: Snapshot = { models: [], loading: true, stale: false };
let snapshot = initial;
let checkedAt = 0;
let inflight: Promise<void> | null = null;
const listeners = new Set<() => void>();
const subscribe = (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; };

export async function refreshWavespeedCatalog() {
  if (inflight) return inflight;
  if (Date.now() - checkedAt < 60_000) return;
  inflight = (async () => {
    try {
      const response = await fetch('/api/ai/wavespeed/models', { cache: 'no-store', signal: AbortSignal.timeout(25_000) });
      const json = await response.json();
      if (!response.ok || !json.success || !Array.isArray(json.data)) throw new Error('Catalog unavailable');
      snapshot = { models: json.data, loading: false, stale: !!json.meta?.stale, updatedAt: json.meta?.updatedAt };
    } catch {
      snapshot = { ...snapshot, loading: false, stale: true };
    } finally {
      checkedAt = Date.now();
      inflight = null;
      listeners.forEach((listener) => listener());
    }
  })();
  return inflight;
}

/** Shared by Studio, Library and the editor; refreshes while open and on return. */
export function useWavespeedCatalog() {
  const state = useSyncExternalStore(subscribe, () => snapshot, () => initial);
  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState !== 'hidden') void refreshWavespeedCatalog();
    };
    refresh();
    const interval = setInterval(refresh, 60_000);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, []);
  return state;
}
